import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Sparkles, AlertTriangle } from "lucide-react";

// Shows a trial countdown nudge, or a past-due "fix payment" prompt, driven by entitlements.
export default function TrialBanner() {
  const { entitlements } = useAuth();
  const navigate = useNavigate();

  if (entitlements?.past_due) {
    const url = entitlements.fix_payment_url;
    const fix = () => { url ? window.open(url, "_blank", "noreferrer") : navigate("/portal/billing"); };
    return (
      <div className="mb-5 flex flex-col gap-3 rounded-xl border border-red-500/40 bg-red-500/10 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between" data-testid="pastdue-banner">
        <div className="flex items-center gap-2.5 text-red-200">
          <AlertTriangle className="h-4 w-4 shrink-0 text-red-400" />
          <span><span className="font-semibold text-white">Your last payment didn't go through.</span> Some features are limited until it's resolved — please update your payment method.</span>
        </div>
        <button onClick={fix} data-testid="pastdue-fix-btn"
          className="shrink-0 rounded-full bg-red-500 px-4 py-1.5 text-xs font-bold text-white transition-colors hover:bg-red-400">
          Fix payment
        </button>
      </div>
    );
  }

  if (entitlements?.trialing && entitlements?.trial_ends) {
    const daysLeft = Math.max(0, Math.ceil((entitlements.trial_ends * 1000 - Date.now()) / 86400000));
    const label = daysLeft <= 0 ? "Your free trial ends today" : `${daysLeft} day${daysLeft === 1 ? "" : "s"} left in your ${entitlements.plan_name} trial`;
    return (
      <div className="mb-5 flex flex-col gap-3 rounded-xl border border-emerald-500/30 bg-emerald-500/10 px-4 py-3 text-sm sm:flex-row sm:items-center sm:justify-between" data-testid="trial-banner">
        <div className="flex items-center gap-2.5 text-emerald-200">
          <Sparkles className="h-4 w-4 shrink-0 text-emerald-400" />
          <span><span className="font-semibold text-white" data-testid="trial-days-left">{label}</span>. {entitlements.keep_confirmed ? "Plan confirmed — it continues automatically when the trial ends." : "Confirm your plan to keep every feature after it ends."}</span>
        </div>
        {!entitlements.keep_confirmed && (
        <button onClick={() => navigate("/portal/billing?keep=1")} data-testid="trial-upgrade-btn"
          className="shrink-0 rounded-full bg-emerald-500 px-4 py-1.5 text-xs font-bold text-slate-900 transition-colors hover:bg-emerald-400">
          Keep my plan
        </button>
        )}
      </div>
    );
  }

  return null;
}
